/** Budget · Bonuses (eskiz 21): recruiter bonuslari status tarixidan hisoblanadi, FM tasdiqlaydi. */
import { Fragment, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { payError, useAdjustBonus, useApproveBonuses, useBonuses, useBonusSummary, useBudgetBranches, useCalculateBonuses, useMarkBonusesPaid } from "@/features/budget/api";
import { money, type Bonus, type BonusStatus } from "@/features/budget/types";
import { periodMonth } from "@/features/dashboards/api";
import { ApiError } from "@/lib/api";
import { cn, formatDate, formatMoney } from "@/lib/utils";
import { Badge, StatusPill } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox, FilterSelect, Input, NumberInput } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { toast } from "@/components/ui/toast";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText } from "@/lib/clipboard";
import { bonusMenu } from "@/features/budget/menu";
import { BudgetLayout, scopeLabel, useBudgetParams } from "./BudgetLayout";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";
import { can } from "@/features/auth/types";
import { PayModal, type PayResult } from "@/features/finance/PayModal";

// Matnlar: i18n namespace "budget" (bonuses.*)
const BONUS_STATUSES: BonusStatus[] = ["draft", "approved", "paid"];

export function BonusesPage() {
  const { t } = useTranslation("budget");
  const { period, branch, isOwner, me } = useBudgetParams();
  const branches = useBudgetBranches();
  const bonuses = useBonuses({ period, branch });
  const summary = useBonusSummary({ period, branch });
  const calculate = useCalculateBonuses();
  const approve = useApproveBonuses();
  const markPaid = useMarkBonusesPaid();
  const [status, setStatus] = useState("");
  const [withBonus, setWithBonus] = useState(true);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // To'lov oynasi: qaysi bonuslar (bitta yoki tanlanganlar) -- qaysi hisobdan to'lanadi
  const [payFor, setPayFor] = useState<Bonus[] | null>(null);
  const month = periodMonth(period);

  const rows = bonuses.data ?? [];
  const filtered = rows.filter((r) => (!status || r.status === status) && (!withBonus || money(r.total) > 0));
  const drafts = filtered.filter((r) => r.status === "draft" && money(r.total) > 0);
  const approvedRows = filtered.filter((r) => r.status === "approved");
  // Jami valyuta bo'yicha: USD va UZS bitta songa qo'shilmaydi (maoshlar sahifasidagi kabi)
  const sumBy = (list: typeof filtered, pick: (r: (typeof filtered)[number]) => number) => {
    const by = list.reduce<Record<string, number>>((m, r) => ({ ...m, [r.currency]: (m[r.currency] ?? 0) + pick(r) }), {});
    const parts = Object.entries(by).filter(([, v]) => v !== 0).map(([c, v]) => formatMoney(v, c));
    return parts.length ? parts.join(" + ") : formatMoney(0);
  };
  const rule = summary.data?.rule;

  async function runCalculate() {
    try {
      const res = await calculate.mutateAsync({ period, branch });
      // Javobda bir recruiterda bir nechta qator bo'lishi mumkin (asosiy + qo'shimcha); yangi qo'shimchalar alohida aytiladi
      const people = new Set(res.map((b) => b.user.id)).size;
      const extra = res.filter((b) => b.part > 1 && b.status === "draft").length;
      toast.success(t("bonuses.calculated", { month }), [t("bonuses.calculatedSub", { count: people }), extra ? t("bonuses.calculatedExtra", { count: extra }) : ""].filter(Boolean).join(" · "));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("bonuses.calcFailed"));
    }
  }

  async function runApprove() {
    try {
      const res = await approve.mutateAsync(drafts);
      toast.success(t("bonuses.approved", { count: res.approved }), t("bonuses.approvedSub"));
      setApproveOpen(false);
    } catch (err) {
      toast.error(payError(err, t, t("bonuses.approveFailed")));
      setApproveOpen(false);
    }
  }

  // O'ng tugma menyusi: bitta bonus uchun (ommaviy tugmalar bilan bir xil API)
  async function approveOne(b: Bonus) {
    try {
      await approve.mutateAsync([b]);
      toast.success(t("bonuses.approved", { count: 1 }), t("bonuses.approvedSub"));
    } catch (err) {
      toast.error(payError(err, t, t("bonuses.approveFailed")));
    }
  }

  function markOnePaid(b: Bonus) {
    setPayFor([b]);
  }

  function runMarkPaid() {
    setPayFor(rows.filter((r) => selected.has(r.id)));
  }

  async function pay(result: PayResult) {
    if (!payFor) return;
    try {
      const res = await markPaid.mutateAsync({ rows: payFor, ...result });
      toast.success(t("bonuses.markedCount", { count: res.paid }));
      setSelected(new Set());
      setPayFor(null);
    } catch (err) {
      toast.error(payError(err, t, t("bonuses.updateFailed")));
      setPayFor(null);
    }
  }

  return (
    <BudgetLayout
      subtitle={me ? t("bonuses.subtitle", { scope: scopeLabel(me, branch, branches.data ?? []), month }) : undefined}
      actions={
        <>
          {selected.size > 0 && (
            <Button onClick={runMarkPaid} loading={markPaid.isPending}>
              {t("bonuses.markPaid", { count: selected.size })}
            </Button>
          )}
          <Button onClick={runCalculate} loading={calculate.isPending}>
            {t("bonuses.calculate", { month })}
          </Button>
          <Button variant="primary" onClick={() => setApproveOpen(true)} disabled={drafts.length === 0}>
            {t("bonuses.approveAll", { count: drafts.length })}
          </Button>
        </>
      }
    >
      {rule && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-card border border-budget bg-budget-soft px-4 py-2 text-[13px]">
          <span className="font-semibold">{t("bonuses.rule", { name: rule.bonus_rule_name })}</span>
          <span>
            {t("bonuses.ruleBase", { perHire: formatMoney(rule.bonus_per_hire, rule.bonus_currency), per30: formatMoney(rule.bonus_per_30_days, rule.bonus_currency) })}
            {rule.bonus_per_60_days > 0 && t("bonuses.rule60", { amount: formatMoney(rule.bonus_per_60_days, rule.bonus_currency) })}
            {rule.bonus_per_90_days > 0 && t("bonuses.rule90", { amount: formatMoney(rule.bonus_per_90_days, rule.bonus_currency) })}
          </span>
          {isOwner && (
            <Link to="/admin/settings" className="ml-auto text-[12px] text-primary hover:underline">
              {t("bonuses.editInAdmin")}
            </Link>
          )}
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterSelect value={status} onChange={(e) => setStatus(e.target.value)} className="w-auto min-w-[150px] max-w-[280px]" aria-label={t("status")}>
          <option value="">{t("bonuses.statusAll")}</option>
          {BONUS_STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(`common:status.${s}`)}
            </option>
          ))}
        </FilterSelect>
        <Checkbox label={t("bonuses.withBonusOnly")} checked={withBonus} onChange={(e) => setWithBonus(e.target.checked)} className="ml-1" />
        {summary.data && (
          <span className="ml-auto text-[12px] text-muted">
            {t("bonuses.summary", { count: summary.data.count, draft: summary.data.draft, approved: summary.data.approved, paid: summary.data.paid })}
          </span>
        )}
      </div>

      {bonuses.isPending ? (
        <PageLoading />
      ) : bonuses.isError ? (
        <ErrorState message={bonuses.error.message} onRetry={() => bonuses.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title={t("bonuses.emptyMonth", { month })} description={t("bonuses.emptyDesc")} action={<Button variant="primary" onClick={runCalculate} loading={calculate.isPending}>{t("bonuses.calculate", { month })}</Button>} />
      ) : filtered.length === 0 ? (
        <EmptyState title={t("bonuses.emptyFiltered")} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th className="w-8">
                <input type="checkbox" className="h-4 w-4 accent-primary" aria-label={t("bonuses.selectApproved")} checked={approvedRows.length > 0 && selected.size === approvedRows.length} onChange={(e) => setSelected(e.target.checked ? new Set(approvedRows.map((r) => r.id)) : new Set())} />
              </Th>
              <Th>{t("bonuses.cols.recruiter")}</Th>
              <Th className="text-right">{t("bonuses.cols.hires")}</Th>
              <Th className="text-center">{t("bonuses.cols.stayed")}</Th>
              <Th>{t("bonuses.cols.rule")}</Th>
              <Th className="text-right">{t("bonuses.cols.calculated")}</Th>
              <Th className="text-right">{t("bonuses.cols.adjustment")}</Th>
              <Th className="text-right">{t("bonuses.cols.total")}</Th>
              <Th>{t("bonuses.cols.status")}</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <Fragment key={b.id}>
                <BonusRow bonus={b} isManager={can(me, "pay")} expanded={expanded === b.id} onToggle={() => setExpanded(expanded === b.id ? null : b.id)} selected={selected.has(b.id)} onSelect={(v) => setSelected((s) => { const n = new Set(s); if (v) n.add(b.id); else n.delete(b.id); return n; })} onApprove={() => void approveOne(b)} onMarkPaid={() => void markOnePaid(b)} />
                {expanded === b.id && (
                  <tr>
                    <Td colSpan={11} className="bg-fill/50">
                      <BonusDetails bonus={b} month={month} />
                    </Td>
                  </tr>
                )}
              </Fragment>
            ))}
            <Tr className="bg-fill/50">
              <Td />
              <Td className="font-bold" colSpan={2}>
                {t("bonuses.totalRow", { count: filtered.length })}
              </Td>
              <Td className="text-right font-bold">{filtered.reduce((s, r) => s + r.hires, 0)}</Td>
              <Td className="text-center font-semibold">
                {filtered.reduce((s, r) => s + r.retained_30, 0)} / {filtered.reduce((s, r) => s + r.retained_60, 0)} / {filtered.reduce((s, r) => s + r.retained_90, 0)}
              </Td>
              <Td />
              <Td className="text-right font-semibold">{sumBy(filtered, (r) => money(r.calculated_amount))}</Td>
              <Td className="text-right font-semibold">{sumBy(filtered, (r) => money(r.adjustment))}</Td>
              <Td className="text-right font-bold">{sumBy(filtered, (r) => money(r.total))}</Td>
              <Td colSpan={2} />
            </Tr>
          </tbody>
        </Table>
      )}

      <Modal
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title={t("bonuses.approveTitle", { count: drafts.length, month })}
        description={t("bonuses.approveDesc")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setApproveOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button variant="primary" loading={approve.isPending} onClick={runApprove}>
              {t("bonuses.approveSum", { sum: sumBy(drafts, (r) => money(r.total)) })}
            </Button>
          </>
        }
      >
        <ul className="max-h-60 space-y-1 overflow-y-auto text-[13px]">
          {drafts.map((d) => (
            <li key={d.id} className="flex justify-between">
              <span>
                {d.user.display_name} <span className="text-muted">{t("bonuses.hiresShort", { count: d.hires })}</span>
              </span>
              <span className="font-semibold">{formatMoney(money(d.total), d.currency)}</span>
            </li>
          ))}
        </ul>
      </Modal>
      <PayModal
        open={!!payFor}
        onOpenChange={(v) => !v && setPayFor(null)}
        title={t("pay.bonusTitle", { count: payFor?.length ?? 0 })}
        description={payFor?.length === 1 ? payFor[0].user.display_name : undefined}
        amounts={(payFor ?? []).map((b) => ({ amount: money(b.total), currency: b.currency }))}
        branch={branch}
        allowNoAccount
        confirmLabel={t("pay.markPaid")}
        loading={markPaid.isPending}
        onConfirm={pay}
      />
    </BudgetLayout>
  );
}

function BonusRow({ bonus: b, isManager, expanded, onToggle, selected, onSelect, onApprove, onMarkPaid }: { bonus: Bonus; isManager: boolean; expanded: boolean; onToggle: () => void; selected: boolean; onSelect: (v: boolean) => void; onApprove: () => void; onMarkPaid: () => void }) {
  const { t } = useTranslation("budget");
  const adjust = useAdjustBonus();
  const [editing, setEditing] = useState(false);
  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const rowMenu = () =>
    bonusMenu(b, isManager, expanded, { toggleDetails: onToggle, adjust: () => setEditing(true), approve: onApprove, markPaid: onMarkPaid, copy: (text) => void copyText(text), profile: personNav }, tm);
  const [amount, setAmount] = useState(String(money(b.adjustment)));
  const [note, setNote] = useState(b.adjustment_note);

  async function save() {
    try {
      await adjust.mutateAsync({ id: b.id, adjustment: amount || "0", adjustment_note: note, version: b.version });
      toast.success(t("bonuses.adjSaved", { name: b.user.display_name }));
      setEditing(false);
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) toast.error(t("conflict", { who: err.changedBy ?? t("someoneElse") }));
      else toast.error(err instanceof ApiError ? err.message : t("bonuses.saveFailed"));
      setEditing(false);
    }
  }

  const adj = money(b.adjustment);
  return (
    <ContextMenu items={() => rowMenu()}>
      <Tr className={cn(expanded && "bg-primary-soft/30")}>
        <Td>{b.status === "approved" ? <input type="checkbox" className="h-4 w-4 accent-primary" aria-label={t("bonuses.select", { name: b.user.display_name })} checked={selected} onChange={(e) => onSelect(e.target.checked)} /> : null}</Td>
        <Td className="font-semibold">
          <PersonLink id={b.user.id} name={b.user.display_name} />
          {b.part > 1 && (
            <Badge tone="blue" className="ml-2 align-middle">
              {t("bonuses.supplement")}
            </Badge>
          )}
        </Td>
        <Td className="text-right">{b.hires}</Td>
        <Td className="text-center">
          {b.retained_30} / {b.retained_60} / {b.retained_90}
        </Td>
        <Td className="text-[12px] text-ink-soft">{b.rule_name}</Td>
        <Td className="text-right">{formatMoney(money(b.calculated_amount), b.currency)}</Td>
        <Td className="text-right">
          {editing ? (
            <span className="flex items-center justify-end gap-1">
              <NumberInput step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-[90px] text-right" aria-label={t("bonuses.adjustment")} />
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("bonuses.notePlaceholder")} className="w-[130px]" aria-label={t("bonuses.adjustmentNote")} />
              <Button size="sm" variant="primary" loading={adjust.isPending} onClick={save}>
                {t("common:actions.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                ×
              </Button>
            </span>
          ) : (
            <span className="inline-flex items-center justify-end gap-2">
              {adj !== 0 ? (
                <span className={cn("font-semibold", adj < 0 ? "text-danger" : "text-success")} title={b.adjustment_note}>
                  {adj > 0 ? "+" : "−"}
                  {formatMoney(Math.abs(adj), b.currency)}
                  {b.adjustment_note && <span className="ml-1 font-normal text-muted">· {b.adjustment_note}</span>}
                </span>
              ) : (
                <span className="text-muted">—</span>
              )}
              {isManager && b.status === "draft" && (
                <button className="text-[12px] text-primary hover:underline" onClick={() => setEditing(true)}>
                  {t("common:actions.edit")}
                </button>
              )}
            </span>
          )}
        </Td>
        <Td className="text-right font-bold">{formatMoney(money(b.total), b.currency)}</Td>
        <Td>
          <StatusPill status={b.status} />
          {b.payment && <div className="mt-0.5 text-[11px] text-muted">{b.payment.account}</div>}
        </Td>
        <Td>
          <button className="text-[12px] text-primary hover:underline" onClick={onToggle}>
            {expanded ? t("bonuses.hide") : t("bonuses.details")}
          </button>
        </Td>
      </Tr>
    </ContextMenu>
  );
}

function BonusDetails({ bonus: b, month }: { bonus: Bonus; month: string }) {
  const { t } = useTranslation("budget");
  const perHire = b.rule.per_hire ?? 0;
  const per30 = b.rule.per_30 ?? 0;
  const stayed = b.details.filter((d) => d.retained_30);
  return (
    <div className="grid grid-cols-1 gap-4 py-2 text-[13px] md:grid-cols-3">
      <div>
        <div className="font-semibold">
          {t("bonuses.d.hiresIn", { month, count: b.hires, per: formatMoney(perHire, b.currency), sum: formatMoney(b.hires * perHire, b.currency) })}
        </div>
        <ul className="mt-1 space-y-0.5 text-ink-soft">
          {b.details.map((d) => (
            <li key={d.driver_id}>
              {t("bonuses.d.hiredOn", { driver: d.driver, date: formatDate(d.hired) })}
              {d.start && <span className="text-muted">{t("bonuses.d.start", { date: formatDate(d.start) })}</span>}
            </li>
          ))}
          {b.details.length === 0 && <li className="text-muted">{t("bonuses.d.noHires")}</li>}
        </ul>
      </div>
      <div>
        <div className="font-semibold">
          {t("bonuses.d.stayed30", { count: b.retained_30, per: formatMoney(per30, b.currency), sum: formatMoney(b.retained_30 * per30, b.currency) })}
        </div>
        <ul className="mt-1 space-y-0.5 text-ink-soft">
          {stayed.map((d) => (
            <li key={d.driver_id}>
              • {d.driver}
              {d.retained_60 && <span className="text-muted">{t("bonuses.d.d60")}</span>}
              {d.retained_90 && <span className="text-muted">{t("bonuses.d.d90")}</span>}
            </li>
          ))}
          {stayed.length === 0 && <li className="text-muted">{t("bonuses.d.noneYet")}</li>}
        </ul>
      </div>
      <div>
        <div className="font-semibold">{t("bonuses.d.result")}</div>
        <ul className="mt-1 space-y-0.5 text-ink-soft">
          <li>{t("bonuses.d.calculated", { amount: formatMoney(money(b.calculated_amount), b.currency) })}</li>
          <li>
            {t("bonuses.d.adjustment", { amount: money(b.adjustment) !== 0 ? formatMoney(money(b.adjustment), b.currency) : "—" })}
            {b.adjustment_note && <span className="text-muted"> · {b.adjustment_note}</span>}
          </li>
          <li className="font-semibold text-ink">{t("bonuses.d.total", { amount: formatMoney(money(b.total), b.currency) })}</li>
          <li>
            {t("bonuses.d.status", { status: t(`common:status.${b.status}`) })}
            {b.approved_by && <span className="text-muted">{t("bonuses.d.by", { name: b.approved_by.display_name })}</span>}
            {b.approved_at && <span className="text-muted"> · {formatDate(b.approved_at)}</span>}
            {b.paid_at && <span className="text-muted">{t("bonuses.d.paid", { date: formatDate(b.paid_at) })}</span>}
          </li>
        </ul>
      </div>
    </div>
  );
}
